import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import useDashboardActiveTab from '../helpers/active-tab.hook';
import rrwebPlayer from 'rrweb-player';
import 'rrweb-player/dist/style.css';
import { apiGetSessionReplay } from 'src/utils/journeyApi';
import { useCurrentUser } from 'src/store/user';
import { useCurrentSubscription } from 'src/store/subscription';
import { useFeatureRestrictions } from 'src/hooks/useFeatureRestrictions';
import { FixedLoadingSpinner } from 'src/common/components/loading-spinner';
import { BreadCrumbsComponent } from 'src/common/breadcrumbs/component';
import { IconButton } from 'src/common/icon-button';
import { LeftStrongIcon } from 'src/monet/icons';
import { BreadcrumbsConfig } from '../insights/constants';
import { useCurrentOrganization } from 'src/store/organization';
import { ExternalLinkIcon } from '@heroicons/react/outline';

type Journey = {
  name: string;
  url: string;
};

export const DashboardSession = () => {

  const { sessionUuid } = useParams();

  const rrWebPlayerElementRef = useRef<any>(null);

  useDashboardActiveTab('sessions');
  const playerRef = useRef<HTMLDivElement>(null);
  const [fetchedSession, setFetchedSession] = useState(false);
  const currentUser = useCurrentUser((state)=>state.currentUser);
  const [journey, setJourney] =  useState<Journey | null>(null)
  const [insightsUrl,setInsightsUrl] = useState<string | null>(null)
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const { subscriptionLoading } = useCurrentSubscription();
  const { features, showUpgradeModal } = useFeatureRestrictions();
  const { currentOrganization } = useCurrentOrganization();
  const history = useHistory()

  const initiateSessionFetch = async (organizationId: any) => {
    try {
      const sessionResponse = await apiGetSessionReplay(organizationId,sessionUuid);
      const events = sessionResponse.events;
      const journey = sessionResponse.journey;
      const insights_url = sessionResponse.insights_page_url;

      setJourney(journey)

      setInsightsUrl(insights_url)

      if (events && playerRef.current) {
        if (!fetchedSession) {
          const localRRWebPlayerElement = new rrwebPlayer({
            target: playerRef.current,
            props: {
              events: events,
              autoPlay: false,
            },
          });
          rrWebPlayerElementRef.current = localRRWebPlayerElement;
          setFetchedSession(true);
        }
      }
    } catch (error) {
      console.error("Failed to fetch session replay:", error);
    }
  };

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (isMounted && !subscriptionLoading && !fetchedSession && currentUser) {
      initiateSessionFetch(currentUser.last_organization.id);
    }
  }, [isMounted, fetchedSession, currentUser, sessionUuid,subscriptionLoading]);

  const breadcrumbsConfig: BreadcrumbsConfig = {
    items: [
      {
        value: 'Sessions',
        onClick: () => {
          history.push(`/${currentOrganization.slug}/sessions`);
        },
      },
      {
        value: sessionUuid || '',
        onClick: () => {
          history.push(`/${currentOrganization.slug}/sessions/${sessionUuid}`);
        },
      }
    ],
  };

  const handlePlayPause = () => {
    if (rrWebPlayerElementRef.current) {
      if (isPlaying) {
        rrWebPlayerElementRef.current.pause();
      } else {
        rrWebPlayerElementRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <>
      <div className='flex flex-col w-full'>
        <>
              <div className=' flex gap-2'>
                <IconButton onClick={() => history.goBack()}>
                  <LeftStrongIcon />
                </IconButton>
                <BreadCrumbsComponent
                  value={breadcrumbsConfig.items[breadcrumbsConfig.items.length - 1].value}
                  items={breadcrumbsConfig.items.map((bci) => bci.value)}
                  itemClassNames={breadcrumbsConfig.items.map((i, index) =>
                    index === breadcrumbsConfig.items.length - 1
                      ? 'max-w-[240px] overflow-hidden whitespace-nowrap text-ellipsis'
                      : ''
                  )}
                  onItemClick={(option, index) => {
                    const bci = breadcrumbsConfig.items[index];
                    bci.onClick && bci.onClick();
                  }}
                />
              </div>

            </>

            <div className='text-bedrock-serif-h1 mt-4'>Recorded Session</div>

        {subscriptionLoading ? (
          <FixedLoadingSpinner/>
        ) : features.canAccessSessionReplay ? (
        <div className='flex flex-col mt-5'>
          {journey && (
             <div className='mb-3'>
             <div className='mt-2 flex'>
               <span className='font-bold w-32'>Journey Name: </span>
               <span>{journey?.name}</span>
             </div>
             <div className='mt-1 flex'>
               <span className='font-bold w-32'>Journey URL: </span>
               <a href={journey?.url} target="_blank" rel="noopener noreferrer" className='text-blue-500 flex items-center underline'>
                 {journey?.url}
                 <ExternalLinkIcon className='ml-1 w-4 h-4' />
               </a>
             </div>
             {
              insightsUrl &&
             <div className='mt-1 flex'>
               <span className='font-bold w-32'>Insights Page: </span>
               <a href={insightsUrl} target="_blank" rel="noopener noreferrer" className='text-blue-500 flex items-center underline'>
                 {insightsUrl}
                 <ExternalLinkIcon className='ml-1 w-4 h-4' />
               </a>
             </div>
              }
             <div
            onClick={handlePlayPause}
            className="relative block mt-3 items-center max-w-max mt-4 mx-auto space-x-1 cursor-pointer text-white bg-gray-700 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 py-2 px-4 border border-transparent text-base font-semibold rounded-md transition duration-150 ease-in-out"
          >
            {isPlaying ? 'Pause Recording' : 'Play Recording'}
          </div>
           </div>
        )}
          <div className='mt-3' ref={playerRef}></div>
        </div>
        ) : (
          <div className='mt-10'>
            <span className='text-neue-journey-medium mb-5'>Upgrade to Scale plan to access session replays</span>
            <img 
              className="mt-3 rounded-md border border-gray-800" 
              src={"https://dashboardcdn.journey.io/dashboard-images/session-recording.png"} 
              width={553} 
              height={398} 
              alt={'Session-Replays'} 
            />
            <div className='mt-4'>
              <button 
                onClick={() => showUpgradeModal('session_replay', 
                  'Session replay is a reconstructed presentation of how a user interacted with your journeys. It captures mouse clicks, movements etc giving you deeper insights.<br/><br/>To unlock session replays, upgrade to the Scale Plan',
                  'Unlock Session Replay'
                )}
                className='btn-bedrock-primary'
              >
                Upgrade to Scale Plan
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
